import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductsContext';
import { 
    MdFavoriteBorder, 
    MdFavorite, 
    MdShoppingCart, 
    MdArrowBack,
    MdChevronLeft,
    MdChevronRight,
    MdLocalShipping,
    MdVerifiedUser,
    MdAutorenew
} from 'react-icons/md';
import './ProductDetails.css';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { addToWishlist, removeFromWishlist, isInWishlist, addToCart } = useAuth();
    const { getProductById, loading: productsLoading } = useProducts();
    
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [product, setProduct] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setCurrentImageIndex(0);
        
        // Try to get product from navigation state first
        const passedProduct = location.state?.product;
        
        if (passedProduct) {
            // If product passed from navigation, use it
            setProduct(normalizeProduct(passedProduct));
        } else if (!productsLoading) {
            // Otherwise, fetch from the live product catalog using productId
            const foundProduct = getProductById(id);
            if (foundProduct) {
                setProduct(normalizeProduct(foundProduct));
            }
        }
    }, [id, location.state, productsLoading, getProductById]);

    // Normalize product data to ensure consistent structure
    const normalizeProduct = (prod) => {
        // Use originalPrice as base
        const originalPrice = prod.originalPrice 
            ? (typeof prod.originalPrice === 'string' 
                ? parseInt(prod.originalPrice.replace(/[₹,]/g, '')) 
                : prod.originalPrice)
            : 0;
        
        // Use discountedPrice from JSON
        const discountedPrice = prod.discountedPrice && prod.discountedPrice !== ''
            ? (typeof prod.discountedPrice === 'string' 
                ? parseInt(prod.discountedPrice.replace(/[₹,]/g, '')) 
                : prod.discountedPrice)
            : originalPrice;
        
        const discount = typeof prod.discount === 'string' 
            ? parseInt(prod.discount) 
            : prod.discount || 0;
        
        const normalizedImages = (
            Array.isArray(prod.images) && prod.images.length > 0
                ? prod.images
                : [prod.image]
        ).filter(Boolean);

        return {
            id: prod.productId || prod.id,
            name: prod.productTitle || prod.title || prod.name,
            category: prod.category || 'women',
            originalPrice: originalPrice,
            discountedPrice: discountedPrice,
            discount: discount,
            inStock: prod.inStock !== undefined ? prod.inStock : true,
            stock: prod.stock || 10,
            images: normalizedImages.length > 0 ? normalizedImages : ['/No-Image-Placeholder.svg'],
            description: prod.productDesc || prod.description || 'Beautiful handwoven textile product.',
            sizes: prod.availableSize || prod.sizes || ['Free Size'],
            material: prod.material || '100% Cotton',
            care: prod.care || 'Hand wash in cold water, Dry in shade',
            features: prod.features || [
                'Authentic handwoven design',
                'Traditional craftsmanship',
                'Comfortable fabric',
                'Fade-resistant colors'
            ],
            specifications: prod.specifications || {
                'Fabric': prod.material || 'Cotton',
                'Pattern': 'Traditional',
                'Occasion': 'Casual, Festive'
            },
            sku: prod.sku || '',
            videos: prod.videos || [],
            videoThumbnail: prod.videoThumbnail || ''
        };
    };

    const imagesToShow = product?.images?.length ? product.images : ['/No-Image-Placeholder.svg'];
    const fallbackImage = '/No-Image-Placeholder.svg';

    useEffect(() => {
        if (currentImageIndex >= imagesToShow.length) {
            setCurrentImageIndex(0);
        }
    }, [currentImageIndex, imagesToShow.length]);

    if (!product) {
        return <div className="product-details-page">Loading...</div>;
    }

    const handlePrevImage = () => {
        setCurrentImageIndex(prev => 
            prev === 0 ? imagesToShow.length - 1 : prev - 1
        );
    };

    const handleNextImage = () => {
        setCurrentImageIndex(prev => 
            prev === imagesToShow.length - 1 ? 0 : prev + 1
        );
    };

    const handleAddToCart = () => {
        if (!selectedSize && product.sizes.length > 1 && !product.sizes.includes('Free Size')) {
            alert('Please select a size');
            return;
        }

        const cartItem = {
            id: product.id,
            title: product.name,
            name: product.name,
            price: product.discountedPrice,
            image: imagesToShow[0],
            size: selectedSize || product.sizes[0],
            quantity: quantity,
            inStock: product.inStock,
            sku: product.sku
        };

        addToCart(cartItem);
        alert('Added to cart!');
    };

    const handleWishlistToggle = () => {
        if (isInWishlist(product.id)) {
            removeFromWishlist(product.id);
        } else {
            const wishlistItem = {
                id: product.id,
                title: product.name,
                name: product.name,
                originalPrice: product.originalPrice,
                discountedPrice: product.discountedPrice,
                discount: product.discount,
                image: imagesToShow[0],
                inStock: product.inStock,
                category: product.category
            };
            addToWishlist(wishlistItem);
        }
    };

    return (
        <div className="product-details-page">
            <button className="back-btn" onClick={() => navigate(-1)}>
                <MdArrowBack size={20} />
                Back
            </button>

            <div className="product-details-container">
                {/* Image Carousel Section */}
                <div className="product-images">
                    <div className="main-image-container">
                        <img 
                            src={imagesToShow[currentImageIndex]} 
                            alt={product.name}
                            className="main-image"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = fallbackImage;
                            }}
                        />
                        
                        {imagesToShow.length > 1 && (
                            <>
                                <button 
                                    className="carousel-btn prev-btn"
                                    onClick={handlePrevImage}
                                >
                                    <MdChevronLeft size={30} />
                                </button>
                                <button 
                                    className="carousel-btn next-btn"
                                    onClick={handleNextImage}
                                >
                                    <MdChevronRight size={30} />
                                </button>
                                
                                <div className="carousel-dots">
                                    {imagesToShow.map((_, index) => (
                                        <span
                                            key={index}
                                            className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(index)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Thumbnail Images */}
                    {imagesToShow.length > 1 && (
                        <div className="thumbnail-container">
                            {imagesToShow.map((image, index) => (
                                <img
                                    key={index}
                                    src={image}
                                    alt={`${product.name} ${index + 1}`}
                                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentImageIndex(index)}
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = fallbackImage;
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Product Info Section */}
                <div className="product-info">
                    <h1 className="product-name">{product.name}</h1>

                    <div className="price-section">
                        {product.discount > 0 ? (
                            <>
                                <span className="original-price">₹{product.originalPrice}</span>
                                <span className="current-price">₹{product.discountedPrice}</span>
                                <span className="discount-badge">{product.discount}% OFF</span>
                            </>
                        ) : (
                            <span className="current-price">₹{product.originalPrice}</span>
                        )}
                    </div>

                    <div className="stock-status">
                        {product.inStock ? (
                            <span className="in-stock">✓ In Stock</span>
                        ) : (
                            <span className="out-of-stock">✗ Out of Stock</span>
                        )}
                    </div>

                    {/* Size Selection */}
                    {product.sizes && product.sizes.length > 0 && (
                        <div className="size-section">
                            <h3>Size</h3>
                            <div className="size-options">
                                {product.sizes.map(size => (
                                    <button
                                        key={size}
                                        className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                                        onClick={() => setSelectedSize(size)}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quantity Selection */}
                    <div className="quantity-section">
                        <h3>Quantity</h3>
                        <div className="quantity-controls">
                            <button 
                                className="qty-btn"
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            >
                                -
                            </button>
                            <span className="qty-value">{quantity}</span>
                            <button 
                                className="qty-btn"
                                onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        <button 
                            className="add-to-cart-btn"
                            onClick={handleAddToCart}
                            disabled={!product.inStock}
                        >
                            <MdShoppingCart size={20} />
                            Add to Cart
                        </button>
                        <button 
                            className="wishlist-btn"
                            onClick={handleWishlistToggle}
                        >
                            {isInWishlist(product.id) ? (
                                <MdFavorite size={24} className="filled" />
                            ) : (
                                <MdFavoriteBorder size={24} />
                            )}
                        </button>
                    </div>

                    {/* Features */}
                    <div className="features-section">
                        <div className="feature-item">
                            <MdLocalShipping size={24} />
                            <div>
                                <h4>Free Shipping</h4>
                                <p>On orders above ₹5,000</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <MdAutorenew size={24} />
                            <div>
                                <h4>No COD available</h4>
                                <p>We don't impose any hidden charges</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <MdVerifiedUser size={24} />
                            <div>
                                <h4>Authentic Product</h4>
                                <p>100% genuine products</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Description Tabs */}
            <div className="product-tabs">
                <div className="tab-content">
                    <div className="tab-section">
                        <h2>Description</h2>
                        <p>{product.description}</p>
                    </div>

                    <div className="tab-section">
                        <h2>Features</h2>
                        <ul className="features-list">
                            {product.features.map((feature, index) => (
                                <li key={index}>{feature}</li>
                            ))}
                        </ul>
                    </div>

                    <div className="tab-section">
                        <h2>Specifications</h2>
                        <table className="specs-table">
                            <tbody>
                                {Object.entries(product.specifications).map(([key, value]) => (
                                    <tr key={key}>
                                        <td className="spec-label">{key}</td>
                                        <td className="spec-value">{value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="tab-section">
                        <h2>Care Instructions</h2>
                        <p>{product.care}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetails;
